import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  FloppyDisk,
  Plus,
  Trash,
  ClockCounterClockwise,
  FilePdf,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

const emptyHotel = () => ({
  hotel_name: "",
  brand: "",
  city: "",
  country: "",
  currency: "USD",
  check_in: "",
  check_out: "",
  nights: 0,
  room_type: "",
  num_rooms: 1,
  meal_plan: "",
  sale_price: 0,
  cost_price: 0,
  confirmation_number: "",
});

const ReservationEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [brands, setBrands] = useState([]);

  const [form, setForm] = useState({
    proposal_id: "",
    proposal_reference: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    trip_title: "",
    hotels: [emptyHotel()],
    booking_status: "Pending",
    payment_status: "Unpaid",
    commission_status: "Pending",
    total_sale: 0,
    total_cost: 0,
    total_margin: 0,
    notes: "",
  });
  const [history, setHistory] = useState([]);
  const [reservationId, setReservationId] = useState("");

  useEffect(() => {
    api.get("/hotels/brands").then((r) => setBrands(r.data)).catch(() => {});
    if (isEditing) {
      api.get(`/reservations/${id}`)
        .then((res) => {
          const r = res.data;
          setForm({
            proposal_id: r.proposal_id || "",
            proposal_reference: r.proposal_reference || "",
            client_name: r.client_name || "",
            client_email: r.client_email || "",
            client_phone: r.client_phone || "",
            trip_title: r.trip_title || "",
            hotels: r.hotels?.length ? r.hotels : [emptyHotel()],
            booking_status: r.booking_status || "Pending",
            payment_status: r.payment_status || "Unpaid",
            commission_status: r.commission_status || "Pending",
            total_sale: r.total_sale || 0,
            total_cost: r.total_cost || 0,
            total_margin: r.total_margin || 0,
            notes: r.notes || "",
          });
          setHistory(r.history || []);
          setReservationId(r.reservation_id || "");
        })
        .catch(() => {
          toast.error("Reservation not found");
          navigate("/reservations");
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEditing, navigate]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateHotel = (idx, field, value) => {
    setForm((prev) => {
      const hotels = prev.hotels.map((h, i) => (i === idx ? { ...h, [field]: value } : h));
      const total_sale = hotels.reduce((sum, h) => sum + (h.sale_price || 0), 0);
      const total_cost = hotels.reduce((sum, h) => sum + (h.cost_price || 0), 0);
      return { ...prev, hotels, total_sale, total_cost, total_margin: total_sale - total_cost };
    });
  };

  const addHotel = () => setForm((prev) => ({ ...prev, hotels: [...prev.hotels, emptyHotel()] }));

  const removeHotel = (idx) => {
    setForm((prev) => {
      const hotels = prev.hotels.filter((_, i) => i !== idx);
      const total_sale = hotels.reduce((sum, h) => sum + (h.sale_price || 0), 0);
      const total_cost = hotels.reduce((sum, h) => sum + (h.cost_price || 0), 0);
      return { ...prev, hotels, total_sale, total_cost, total_margin: total_sale - total_cost };
    });
  };

  const handleSave = async () => {
    if (!form.client_name) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/reservations/${id}`, form);
        toast.success("Reservation updated");
      } else {
        const res = await api.post("/reservations", form);
        toast.success("Reservation created");
        navigate(`/reservations/${res.data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  const downloadConfirmation = async (hotelIdx, showPricing = null) => {
    if (!isEditing) {
      toast.error("Please save the reservation first");
      return;
    }
    // If showPricing not explicitly set, ask via a simple prompt
    if (showPricing === null) {
      const include = window.confirm("Include pricing in the confirmation letter?\n\nOK = Yes, show prices\nCancel = No, hide prices");
      showPricing = include;
    }
    try {
      const res = await api.get(`/reservations/${id}/confirmation-pdf/${hotelIdx}?show_pricing=${showPricing}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const hotel = form.hotels[hotelIdx];
      a.download = `Confirmation_${(hotel?.hotel_name || "Hotel").replace(/\s/g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Confirmation letter downloaded");
    } catch {
      toast.error("Failed to generate confirmation PDF");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="reservation-editor">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <Button variant="ghost" size="sm" onClick={() => navigate("/reservations")} data-testid="back-to-reservations" className="shrink-0">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-1 truncate">
            {isEditing ? reservationId : "New Reservation"}
          </p>
          <h1 className="font-serif text-xl sm:text-2xl md:text-3xl text-navy">
            {isEditing ? "Edit Reservation" : "Create Reservation"}
          </h1>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-navy hover:bg-[#2a3a5e] text-white gap-1.5 shrink-0 text-xs md:text-sm" data-testid="save-reservation-btn">
          <FloppyDisk size={16} /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white border border-border p-2 md:p-4 text-center">
          <p className="text-[8px] md:text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total Sale</p>
          <p className="font-serif text-sm md:text-xl text-navy" data-testid="total-sale">{formatCurrency(form.total_sale)}</p>
        </div>
        <div className="bg-white border border-border p-2 md:p-4 text-center">
          <p className="text-[8px] md:text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total Cost</p>
          <p className="font-serif text-sm md:text-xl text-navy" data-testid="total-cost">{formatCurrency(form.total_cost)}</p>
        </div>
        <div className="bg-white border border-border p-2 md:p-4 text-center">
          <p className="text-[8px] md:text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Margin</p>
          <p className={`font-serif text-sm md:text-xl ${form.total_margin >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="total-margin">
            {formatCurrency(form.total_margin)}
          </p>
        </div>
      </div>

      {/* Status Section */}
      <div className="bg-white border border-border p-4 md:p-5 mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 md:mb-4">Status</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Booking Status</Label>
            <select
              value={form.booking_status}
              onChange={(e) => updateField("booking_status", e.target.value)}
              className="w-full text-sm border border-border px-3 py-2 bg-white mt-1"
              data-testid="booking-status-select"
            >
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Payment Status</Label>
            <select
              value={form.payment_status}
              onChange={(e) => updateField("payment_status", e.target.value)}
              className="w-full text-sm border border-border px-3 py-2 bg-white mt-1"
              data-testid="payment-status-select"
            >
              <option value="Unpaid">Unpaid</option>
              <option value="Partial">Partial</option>
              <option value="Paid">Paid</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Commission Status</Label>
            <select
              value={form.commission_status}
              onChange={(e) => updateField("commission_status", e.target.value)}
              className="w-full text-sm border border-border px-3 py-2 bg-white mt-1"
              data-testid="commission-status-select"
            >
              <option value="Pending">Pending</option>
              <option value="Invoiced">Invoiced</option>
              <option value="Received">Received</option>
            </select>
          </div>
        </div>
      </div>

      {/* Client Info */}
      <div className="bg-white border border-border p-4 md:p-5 mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 md:mb-4">Client Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Client Name *</Label>
            <Input value={form.client_name} onChange={(e) => updateField("client_name", e.target.value)} className="mt-1 text-sm" data-testid="client-name-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input value={form.client_email} onChange={(e) => updateField("client_email", e.target.value)} className="mt-1 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Phone</Label>
            <Input value={form.client_phone} onChange={(e) => updateField("client_phone", e.target.value)} className="mt-1 text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mt-3 md:mt-4">
          <div>
            <Label className="text-xs text-muted-foreground">Trip Title</Label>
            <Input value={form.trip_title} onChange={(e) => updateField("trip_title", e.target.value)} className="mt-1 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Linked Proposal</Label>
            <Input value={form.proposal_reference} readOnly className="mt-1 text-sm bg-cream/50" />
          </div>
        </div>
      </div>

      {/* Hotels / Bookings */}
      <div className="bg-white border border-border p-4 md:p-5 mb-4">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hotel Bookings</h3>
          <Button variant="outline" size="sm" onClick={addHotel} className="gap-1 text-xs h-7" data-testid="add-hotel-booking">
            <Plus size={12} /> Add Hotel
          </Button>
        </div>
        <div className="space-y-4">
          {form.hotels.map((hotel, idx) => (
            <div key={idx} className="border border-border p-4 bg-cream/30" data-testid={`hotel-booking-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-navy">Hotel {idx + 1}{hotel.hotel_name ? ` — ${hotel.hotel_name}` : ""}</span>
                <div className="flex items-center gap-1">
                  {isEditing && hotel.hotel_name && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-gold hover:text-gold/80 gap-1"
                      onClick={() => downloadConfirmation(idx)}
                      data-testid={`download-confirmation-${idx}`}
                    >
                      <FilePdf size={12} /> Confirmation
                    </Button>
                  )}
                  {form.hotels.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground hover:text-red-600" onClick={() => removeHotel(idx)}>
                      <Trash size={12} />
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <div className="col-span-2">
                  <Label className="text-[10px] text-muted-foreground">Hotel Name</Label>
                  <Input value={hotel.hotel_name} onChange={(e) => updateHotel(idx, "hotel_name", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Brand</Label>
                  <Input
                    value={hotel.brand}
                    onChange={(e) => updateHotel(idx, "brand", e.target.value)}
                    className="text-xs h-8 mt-0.5"
                    list={`brand-list-${idx}`}
                    placeholder="e.g. Marriott"
                  />
                  <datalist id={`brand-list-${idx}`}>
                    {brands.map((b) => <option key={b} value={b} />)}
                  </datalist>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">City</Label>
                  <Input value={hotel.city} onChange={(e) => updateHotel(idx, "city", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Country</Label>
                  <Input value={hotel.country} onChange={(e) => updateHotel(idx, "country", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Room Type</Label>
                  <Input value={hotel.room_type} onChange={(e) => updateHotel(idx, "room_type", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Rooms</Label>
                  <Input type="number" value={hotel.num_rooms} onChange={(e) => updateHotel(idx, "num_rooms", parseInt(e.target.value) || 1)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Meal Plan</Label>
                  <Input value={hotel.meal_plan} onChange={(e) => updateHotel(idx, "meal_plan", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Check In</Label>
                  <Input type="date" value={hotel.check_in} onChange={(e) => updateHotel(idx, "check_in", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Check Out</Label>
                  <Input type="date" value={hotel.check_out} onChange={(e) => updateHotel(idx, "check_out", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Nights</Label>
                  <Input type="number" value={hotel.nights} onChange={(e) => updateHotel(idx, "nights", parseInt(e.target.value) || 0)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Confirmation #</Label>
                  <Input value={hotel.confirmation_number} onChange={(e) => updateHotel(idx, "confirmation_number", e.target.value)} className="text-xs h-8 mt-0.5" placeholder="Hotel conf. number" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Currency</Label>
                  <Input value={hotel.currency} onChange={(e) => updateHotel(idx, "currency", e.target.value)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Sale Price</Label>
                  <Input type="number" value={hotel.sale_price || ""} onChange={(e) => updateHotel(idx, "sale_price", parseFloat(e.target.value) || 0)} className="text-xs h-8 mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Cost Price</Label>
                  <Input type="number" value={hotel.cost_price || ""} onChange={(e) => updateHotel(idx, "cost_price", parseFloat(e.target.value) || 0)} className="text-xs h-8 mt-0.5" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white border border-border p-4 md:p-5 mb-4">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => updateField("notes", e.target.value)}
          className="mt-2 text-sm min-h-[80px]"
          placeholder="Internal notes about this reservation..."
          data-testid="reservation-notes"
        />
      </div>

      {/* History Log */}
      {history.length > 0 && (
        <div className="bg-white border border-border p-4 md:p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
            <ClockCounterClockwise size={14} /> History Log
          </h3>
          <div className="space-y-2">
            {history.map((entry, idx) => (
              <div key={idx} className="flex items-start gap-3 text-xs py-2 border-b border-border last:border-0">
                <div className="w-2 h-2 bg-gold rounded-full mt-1.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-navy">{entry.action}</span>
                  {entry.details && <span className="text-muted-foreground"> — {entry.details}</span>}
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {entry.timestamp ? format(new Date(entry.timestamp), "MMM d, yyyy HH:mm") : ""} · {entry.user_name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationEditorPage;
